import React, { useMemo, useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { clearAdminSession } from '../auth/adminLogout';
import AdminSidebar from '../components/admin/AdminSidebar';
import { ADMIN_NAV_LINKS } from '../components/admin/adminNavConfig';
import { useServices } from '../contexts/ServiceContext';
import ServiceCard from '../components/ServiceCard';
import AddServiceModal from '../components/AddServiceModal';
import EditServiceModal from '../components/EditServiceModal';
import { useGallery } from '../contexts/ImageContext';
import GalleryCard from '../components/GalleryCard';
import AddImageModal from '../components/AddImageModal';
import FilterTabs from '../components/FilterTabs';
import LightboxModal from '../components/LightboxModal';
import { useProducts } from '../contexts/ProductContext';
import ProductSlider from '../components/ProductSlider';
import AddProductModal from '../components/AddProductModal';
import EditProductModal from '../components/EditProductModal';
import ProductPreviewModal from '../components/ProductPreviewModal';
import { useConsultations } from '../contexts/ConsultationContext';

const ACCENT = '#C8A96A';
function MiniBars({ values, positive }) {
  const max = Math.max(...values, 1);
  return (
    <div className="flex items-end gap-0.5 h-10 mt-2">
      {values.map((v, i) => (
        <div
          key={i}
          className="w-1.5 rounded-sm transition-all"
          style={{
            height: `${Math.max(8, (v / max) * 100)}%`,
            backgroundColor: positive ? '#22c55e' : '#f87171',
            opacity: 0.65 + (i / values.length) * 0.35,
          }}
        />
      ))}
    </div>
  );
}

function RingGauge({ value, label, sublabel, dark }) {
  const pct = Math.min(100, Math.max(0, value));
  const r = 36;
  const c = 2 * Math.PI * r;
  const dash = (pct / 100) * c;
  return (
    <div
      className={`rounded-2xl p-5 flex items-center gap-4 border ${
        dark ? 'border-white/10 bg-black/20' : 'border-stone-200 bg-white'
      }`}
    >
      <div className="relative w-24 h-24 shrink-0">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r={r} fill="none" stroke={dark ? 'rgba(255,255,255,0.12)' : '#e7e5e4'} strokeWidth="8" />
          <circle
            cx="50"
            cy="50"
            r={r}
            fill="none"
            stroke={ACCENT}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={`${dash} ${c}`}
          />
        </svg>
        <div
          className={`absolute inset-0 flex flex-col items-center justify-center text-center ${
            dark ? 'text-white' : 'text-stone-800'
          }`}
        >
          <span className="text-lg font-bold leading-none">{pct.toFixed(0)}</span>
          {sublabel && <span className="text-[10px] opacity-70 mt-0.5">{sublabel}</span>}
        </div>
      </div>
      <div>
        <p className={`text-sm font-semibold ${dark ? 'text-white' : 'text-stone-800'}`}>{label}</p>
        <p className={`text-xs mt-1 ${dark ? 'text-white/60' : 'text-stone-500'}`}>From your live catalogue data</p>
      </div>
    </div>
  );
}

function SemiGauge({ percent }) {
  const p = Math.min(100, Math.max(0, percent));
  const angle = (p / 100) * 180;
  return (
    <div className="flex flex-col items-center justify-center py-4">
      <div className="relative w-44 h-24 overflow-hidden">
        <svg viewBox="0 0 120 70" className="w-full h-full">
          <path
            d="M 10 60 A 50 50 0 0 1 110 60"
            fill="none"
            stroke="#e7e5e4"
            strokeWidth="10"
            strokeLinecap="round"
          />
          <path
            d="M 10 60 A 50 50 0 0 1 110 60"
            fill="none"
            stroke="#22c55e"
            strokeWidth="10"
            strokeLinecap="round"
            strokeDasharray={`${(angle / 180) * 157} 157`}
          />
        </svg>
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 text-center">
          <span className="text-2xl font-bold text-stone-800">{p}%</span>
        </div>
      </div>
      <p className="text-xs text-stone-500 mt-2 text-center px-4">Portfolio sections with published items</p>
    </div>
  );
}

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { pathname, hash } = useLocation();
  const { services, deleteService, addService, updateService } = useServices();
  const [isAddServiceOpen, setIsAddServiceOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [isAddGalleryOpen, setIsAddGalleryOpen] = useState(false);
  const [galleryCategory, setGalleryCategory] = useState('All');
  const [filteredGallery, setFilteredGallery] = useState([]);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [isAddProductOpen, setIsAddProductOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [previewingProduct, setPreviewingProduct] = useState(null);

  const galleryCategories = ['All', 'Living', 'Bedroom', 'Kitchen', 'Office', 'Bathroom'];
  const { galleries, addImage, deleteImage } = useGallery();
  const { products, addProduct, updateProduct, deleteProduct } = useProducts();
  const { consultations } = useConsultations();
  const [displayUser, setDisplayUser] = useState({ name: 'Admin', email: '' });

  useEffect(() => {
    try {
      const raw = localStorage.getItem('user_data');
      if (raw) {
        const u = JSON.parse(raw);
        setDisplayUser({
          name: u.displayName || u.name || 'Admin',
          email: u.email || '',
        });
      }
    } catch {
      /* ignore */
    }
  }, []);

  const stats = useMemo(
    () => ({
      totalServices: services?.length || 0,
      totalGallery: galleries?.length || 0,
      totalProducts: products?.length || 0,
      totalConsultations: consultations?.length || 0,
    }),
    [services, galleries, products, consultations]
  );

  const totalItems =
    stats.totalServices + stats.totalGallery + stats.totalProducts + stats.totalConsultations || 1;

  const mix = useMemo(() => {
    const s = [
      { label: 'Services', n: stats.totalServices, color: '#3b82f6' },
      { label: 'Gallery', n: stats.totalGallery, color: '#22c55e' },
      { label: 'Products', n: stats.totalProducts, color: '#f59e0b' },
      { label: 'Consultations', n: stats.totalConsultations, color: '#ef4444' },
    ];
    return s.map((x) => ({
      ...x,
      pct: Math.round((x.n / totalItems) * 100),
    }));
  }, [stats, totalItems]);

  const sectionsFilled = [
    stats.totalServices > 0,
    stats.totalGallery > 0,
    stats.totalProducts > 0,
    stats.totalConsultations > 0,
  ].filter(Boolean).length;
  const portfolioPct = Math.round((sectionsFilled / 4) * 100);

  const activityScore = Math.min(
    100,
    Math.round(
      Math.log10(totalItems + 1) * 28 + (stats.totalConsultations > 0 ? 22 : 0) + sectionsFilled * 12
    )
  );

  const sparkFrom = (n) => {
    const base = [0.35, 0.52, 0.48, 0.61, 0.55, 0.7, 0.85].map((x) => x * (8 + n * 3));
    return base.map((v) => Math.round(v));
  };

  const handleLogout = async () => {
    await clearAdminSession();
    navigate('/admin/login');
  };

  const showServicesPanel = pathname === '/dashboard' && hash === '#services';
  const showGalleryPanel = pathname === '/dashboard' && hash === '#gallery';
  const showProductsPanel = pathname === '/dashboard' && hash === '#products';

  useEffect(() => {
    if (galleryCategory === 'All') {
      setFilteredGallery(galleries);
    } else {
      setFilteredGallery(galleries.filter((img) => img.category === galleryCategory));
    }
  }, [galleryCategory, galleries]);

  const websiteModules = useMemo(() => {
    const rows = [
      { name: 'Design services', pct: Math.min(99, 18 + stats.totalServices * 6), n: stats.totalServices },
      { name: 'Portfolio gallery', pct: Math.min(99, 15 + stats.totalGallery * 4), n: stats.totalGallery },
      { name: 'Product catalogue', pct: Math.min(99, 12 + stats.totalProducts * 5), n: stats.totalProducts },
      { name: 'Consultation inbox', pct: Math.min(99, 20 + stats.totalConsultations * 8), n: stats.totalConsultations },
    ];
    return rows.sort((a, b) => b.n - a.n);
  }, [stats]);

  return (
    <div className="min-h-screen flex bg-[#f4f6f8] text-stone-800">
      <AdminSidebar />

      {/* Main */}
      <main className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b border-stone-200 px-4 md:px-8 py-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-stone-900 tracking-tight">Dashboard</h1>
            <p className="text-xs text-stone-500 mt-0.5 hidden sm:block">
              Overview of services, gallery, products, and consultations on your site
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border border-stone-200 text-stone-700 hover:bg-stone-50"
            >
              <span className="material-symbols-outlined text-lg">language</span>
              Live site
            </Link>
          </div>
        </header>

        {/* Mobile nav strip */}
        <div className="md:hidden flex gap-2 overflow-x-auto px-4 py-3 border-b border-stone-200 bg-white no-scrollbar">
          {ADMIN_NAV_LINKS.map((item) => (
            <Link
              key={item.label}
              to={item.to}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border ${
                item.isActive(pathname, hash) ? 'border-amber-700/40 bg-amber-50 text-amber-900' : 'border-stone-200 text-stone-600'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </div>

        <div className="flex-1 p-4 md:p-8 space-y-6 md:space-y-8 overflow-auto pb-12">
          {showServicesPanel ? (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard')}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-stone-600 hover:text-stone-900 mb-2"
                  >
                    <span className="material-symbols-outlined text-lg">arrow_back</span>
                    Dashboard overview
                  </button>
                  <h2 className="text-lg font-bold text-stone-900">Manage services</h2>
                  <p className="text-xs text-stone-500 mt-1">Add, edit, or remove services — same list as on your public Services page.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsAddServiceOpen(true)}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white shrink-0"
                  style={{ backgroundColor: ACCENT }}
                >
                  <span className="material-symbols-outlined text-lg">add</span>
                  Add Service
                </button>
              </div>

              {services.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-8">
                  {services.map((service) => (
                    <ServiceCard
                      key={service.id}
                      service={service}
                      onEdit={(s) => setEditingService(s)}
                      onDelete={(s) => {
                        if (window.confirm(`Delete "${s.title}"?`)) {
                          deleteService(s.id);
                        }
                      }}
                    />
                  ))}
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center shadow-sm">
                  <span className="material-symbols-outlined text-5xl text-stone-300 mb-3 block">design_services</span>
                  <p className="text-stone-600 font-medium">No services yet</p>
                  <p className="text-sm text-stone-500 mt-1">Use Add Service to create your first one.</p>
                </div>
              )}

              <AddServiceModal
                isOpen={isAddServiceOpen}
                onClose={() => setIsAddServiceOpen(false)}
                onAddService={(data) => addService(data)}
              />
              <EditServiceModal
                isOpen={!!editingService}
                onClose={() => setEditingService(null)}
                service={editingService}
                onEditService={(data) => updateService(data.id, data)}
              />
            </div>
          ) : showGalleryPanel ? (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard')}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-stone-600 hover:text-stone-900 mb-2"
                  >
                    <span className="material-symbols-outlined text-lg">arrow_back</span>
                    Dashboard overview
                  </button>
                  <h2 className="text-lg font-bold text-stone-900">Manage gallery</h2>
                  <p className="text-xs text-stone-500 mt-1">Add or remove portfolio images — same data as your public Gallery page.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsAddGalleryOpen(true)}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white shrink-0"
                  style={{ backgroundColor: ACCENT }}
                >
                  <span className="material-symbols-outlined text-lg">add_photo_alternate</span>
                  Add image
                </button>
              </div>

              <div className="bg-white rounded-2xl border border-stone-200 p-4 shadow-sm">
                <FilterTabs
                  categories={galleryCategories}
                  activeCategory={galleryCategory}
                  onCategoryChange={setGalleryCategory}
                />
              </div>

              {filteredGallery.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
                  {filteredGallery.map((image) => (
                    <GalleryCard
                      key={image.id}
                      image={image}
                      onImageClick={(img) => setLightboxImage(img)}
                      onDeleteClick={(img) => {
                        if (window.confirm(`Delete "${img.title}"?`)) {
                          deleteImage(img.id);
                          setLightboxImage((cur) => (cur?.id === img.id ? null : cur));
                        }
                      }}
                    />
                  ))}
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center shadow-sm">
                  <span className="material-symbols-outlined text-5xl text-stone-300 mb-3 block">collections</span>
                  <p className="text-stone-600 font-medium">No images in this filter</p>
                  <p className="text-sm text-stone-500 mt-1">Add an image or pick another category.</p>
                </div>
              )}

              <AddImageModal
                isOpen={isAddGalleryOpen}
                onClose={() => setIsAddGalleryOpen(false)}
                onAddImage={(data) => {
                  addImage(data);
                  setIsAddGalleryOpen(false);
                }}
              />

              {lightboxImage && (
                <LightboxModal
                  image={lightboxImage}
                  images={filteredGallery}
                  onClose={() => setLightboxImage(null)}
                  onNext={() => {
                    const idx = filteredGallery.findIndex((img) => img.id === lightboxImage.id);
                    if (idx === -1) return;
                    setLightboxImage(filteredGallery[(idx + 1) % filteredGallery.length]);
                  }}
                  onPrev={() => {
                    const idx = filteredGallery.findIndex((img) => img.id === lightboxImage.id);
                    if (idx === -1) return;
                    setLightboxImage(
                      filteredGallery[idx === 0 ? filteredGallery.length - 1 : idx - 1]
                    );
                  }}
                />
              )}
            </div>
          ) : showProductsPanel ? (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard')}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-stone-600 hover:text-stone-900 mb-2"
                  >
                    <span className="material-symbols-outlined text-lg">arrow_back</span>
                    Dashboard overview
                  </button>
                  <h2 className="text-lg font-bold text-stone-900">Manage products</h2>
                  <p className="text-xs text-stone-500 mt-1">
                    Add, edit, preview, or remove catalogue items — same list as on the homepage collection.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsAddProductOpen(true)}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white shrink-0"
                  style={{ backgroundColor: ACCENT }}
                >
                  <span className="material-symbols-outlined text-lg">add</span>
                  Add product
                </button>
              </div>

              <div
                className="rounded-2xl border border-stone-200 p-4 sm:p-6 md:p-8 shadow-sm"
                style={{ backgroundColor: '#F5EFE6' }}
              >
                {products.length > 0 ? (
                  <ProductSlider
                    products={products}
                    onPreview={(p) => setPreviewingProduct(p)}
                    onEdit={(p) => setEditingProduct(p)}
                    onDelete={(p) => {
                      if (window.confirm(`Delete "${p.name}"?`)) {
                        deleteProduct(p.id);
                        setPreviewingProduct((cur) => (cur?.id === p.id ? null : cur));
                        setEditingProduct((cur) => (cur?.id === p.id ? null : cur));
                      }
                    }}
                  />
                ) : (
                  <div className="text-center py-16">
                    <span className="material-symbols-outlined text-6xl text-stone-300 mb-4 block">chair</span>
                    <h3 className="text-xl font-semibold mb-2" style={{ color: '#3E3E3E' }}>
                      No products yet
                    </h3>
                    <p style={{ color: '#7A7A7A' }}>Use Add product to create your first one.</p>
                  </div>
                )}
              </div>

              <AddProductModal
                isOpen={isAddProductOpen}
                onClose={() => setIsAddProductOpen(false)}
                onAddProduct={addProduct}
              />
              <EditProductModal
                isOpen={!!editingProduct}
                onClose={() => setEditingProduct(null)}
                onEditProduct={updateProduct}
                product={editingProduct}
              />
              <ProductPreviewModal
                isOpen={!!previewingProduct}
                onClose={() => setPreviewingProduct(null)}
                product={previewingProduct}
              />
            </div>
          ) : (
            <>
          {/* Top stat row — like reference KPI cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
            <button
              type="button"
              onClick={() => navigate('/dashboard#services')}
              className="bg-white rounded-2xl border border-stone-200 p-5 shadow-sm text-left w-full cursor-pointer hover:border-stone-300 hover:shadow transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-amber-500/50"
            >
              <p className="text-sm font-medium text-stone-500">Published services</p>
              <p className="text-3xl font-bold text-stone-900 mt-1 tabular-nums">{stats.totalServices}</p>
              <MiniBars values={sparkFrom(stats.totalServices)} positive />
              <p className="text-xs text-stone-400 mt-2">Click to manage on this dashboard — or view on /services</p>
            </button>
            <button
              type="button"
              onClick={() => navigate('/dashboard#gallery')}
              className="bg-white rounded-2xl border border-stone-200 p-5 shadow-sm text-left w-full cursor-pointer hover:border-stone-300 hover:shadow transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-amber-500/50"
            >
              <p className="text-sm font-medium text-stone-500">Gallery projects</p>
              <p className="text-3xl font-bold text-stone-900 mt-1 tabular-nums">{stats.totalGallery}</p>
              <MiniBars values={sparkFrom(stats.totalGallery).map((v) => v * 0.9)} positive={stats.totalGallery >= 3} />
              <p className="text-xs text-stone-400 mt-2">Click to manage on this dashboard — or view on /gallery</p>
            </button>
            <div className="bg-white rounded-2xl border border-stone-200 p-5 shadow-sm sm:col-span-2 xl:col-span-1">
              <p className="text-sm font-medium text-stone-500">Consultation requests</p>
              <p className="text-3xl font-bold text-stone-900 mt-1 tabular-nums">{stats.totalConsultations}</p>
              <MiniBars values={sparkFrom(stats.totalConsultations)} positive />
              <p className="text-xs text-stone-400 mt-2">From your contact flow — review on the Contact page</p>
            </div>
          </div>

          {/* Middle row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
            <div className="bg-white rounded-2xl border border-stone-200 p-5 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-bold text-stone-800">Content coverage</h2>
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">Live</span>
              </div>
              <SemiGauge percent={portfolioPct} />
            </div>

            <div className="bg-white rounded-2xl border border-stone-200 p-5 shadow-sm">
              <h2 className="text-sm font-bold text-stone-800 mb-4">Catalogue mix</h2>
              <p className="text-4xl font-bold text-stone-900 mb-1">{totalItems}</p>
              <p className="text-xs text-stone-500 mb-4">Total indexed items across sections</p>
              <ul className="space-y-3">
                {mix.map((row) => (
                  <li key={row.label}>
                    {row.label === 'Products' ? (
                      <button
                        type="button"
                        onClick={() => navigate('/dashboard#products')}
                        className="flex w-full items-center justify-between text-sm rounded-lg -mx-1 px-1 py-1 text-left hover:bg-stone-50 transition-colors cursor-pointer"
                      >
                        <span className="flex items-center gap-2 text-stone-600">
                          <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: row.color }} />
                          {row.label}
                          <span className="text-[10px] font-normal text-stone-400">(manage)</span>
                        </span>
                        <span className="font-semibold text-stone-800 tabular-nums">{row.pct}%</span>
                      </button>
                    ) : (
                      <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2 text-stone-600">
                          <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: row.color }} />
                          {row.label}
                        </span>
                        <span className="font-semibold text-stone-800 tabular-nums">{row.pct}%</span>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => navigate('/services')}
                className="mt-5 w-full py-2.5 rounded-xl text-sm font-semibold border border-stone-200 text-stone-700 hover:bg-stone-50"
              >
                View details on site
              </button>
            </div>

            <div className="bg-white rounded-2xl border border-stone-200 p-5 shadow-sm lg:col-span-1">
              <h2 className="text-sm font-bold text-stone-800 mb-1">Website modules</h2>
              <p className="text-xs text-stone-500 mb-4">Strength by how much content each area has</p>
              <ul className="space-y-3">
                {websiteModules.map((row) => (
                  <li key={row.name}>
                    {row.name === 'Product catalogue' ? (
                      <button
                        type="button"
                        onClick={() => navigate('/dashboard#products')}
                        className="w-full text-left rounded-lg -mx-1 px-1 py-0.5 hover:bg-stone-50 transition-colors"
                      >
                        <div className="flex justify-between text-xs font-medium text-stone-600 mb-1">
                          <span>
                            {row.name}
                            <span className="text-[10px] font-normal text-stone-400 ml-1">(manage)</span>
                          </span>
                          <span className="tabular-nums">{row.pct}%</span>
                        </div>
                        <div className="h-2 rounded-full bg-stone-100 overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{ width: `${row.pct}%`, backgroundColor: ACCENT }}
                          />
                        </div>
                      </button>
                    ) : (
                      <>
                        <div className="flex justify-between text-xs font-medium text-stone-600 mb-1">
                          <span>{row.name}</span>
                          <span className="tabular-nums">{row.pct}%</span>
                        </div>
                        <div className="h-2 rounded-full bg-stone-100 overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{ width: `${row.pct}%`, backgroundColor: ACCENT }}
                          />
                        </div>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Bottom row */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
            <RingGauge value={activityScore} sublabel="" label="Engagement index" dark />
            <RingGauge value={Math.min(100, portfolioPct + (stats.totalProducts > 0 ? 8 : 0))} label="Studio readiness" dark={false} />

            <Link
              to="/contact"
              className="rounded-2xl p-6 flex flex-col justify-between min-h-[160px] relative overflow-hidden group border border-emerald-800/20"
              style={{
                background: `linear-gradient(135deg, #0f3d32 0%, #165e4d 45%, ${ACCENT} 180%)`,
              }}
            >
              <span
                className="material-symbols-outlined absolute right-4 top-4 text-white/40 group-hover:text-white/80 transition-colors"
                aria-hidden
              >
                arrow_outward
              </span>
              <div className="relative z-10">
                <p className="text-white/80 text-xs uppercase tracking-widest font-semibold mb-2">Client enquiries</p>
                <h3 className="text-xl font-bold text-white leading-snug">Let&apos;s design your next space</h3>
                <p className="text-sm text-white/85 mt-2 max-w-xs">
                  Open the contact page to see how customers reach you and follow up from your inbox.
                </p>
              </div>
              <div className="relative z-10 flex items-center gap-2 mt-4">
                <div className="flex -space-x-2">
                  {[0, 1, 2].map((i) => (
                    <span
                      key={i}
                      className="w-8 h-8 rounded-full bg-white/20 border-2 border-white/30 flex items-center justify-center"
                    >
                      <span className="material-symbols-outlined text-white text-sm">person</span>
                    </span>
                  ))}
                </div>
                <span className="text-xs font-medium text-white/90">Panipat · NCR · residential & commercial</span>
              </div>
            </Link>
          </div>
            </>
          )}
        </div>

        {/* Mobile sign out */}
        <div className="md:hidden sticky bottom-0 p-4 bg-white border-t border-stone-200 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-stone-800 truncate">{displayUser.name}</p>
            <p className="text-xs text-stone-500 truncate">{displayUser.email}</p>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="shrink-0 px-4 py-2 rounded-lg text-sm font-semibold bg-stone-900 text-white"
          >
            Sign out
          </button>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
